/*
  noma-gallery.js: <noma-gallery> custom element (design variant, product photo block).
  Markup: theme/design/snippets/product-media-gallery.liquid. Styles: noma-design-gallery.css.

  - The main image row is a native scroll-snap scroller (swipe / trackpad work without this script); this element adds the
    thumbnail buttons, prev / next, keyboard, a live region and the lightbox (a <dialog>) and keeps them in sync.
  - Never throws when Dawn's globals (pauseAllMedia, subscribe, PUB_SUB_EVENTS) are missing.
  - Variant change: Dawn's product-info publishes variant-change; the variant's featured media is shown.
*/
(function () {
  if (customElements.get('noma-gallery')) return;

  var SWIPE_DISTANCE = 48;

  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function pauseMedia() {
    if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
  }

  /* Where an object-fit: contain image is really drawn inside its box (the whole box until the file has loaded). */
  function paintedRect(img) {
    var box = img.getBoundingClientRect();
    var width = img.naturalWidth;
    var height = img.naturalHeight;
    if (!width || !height || !box.width || !box.height) return box;
    var scale = Math.min(box.width / width, box.height / height);
    var w = width * scale;
    var h = height * scale;
    var left = box.left + (box.width - w) / 2;
    var top = box.top + (box.height - h) / 2;
    return { left: left, top: top, right: left + w, bottom: top + h };
  }

  class NomaGallery extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.init();
    }

    disconnectedCallback() {
      this.destroy();
    }

    init() {
      this.track = this.querySelector('[data-track]');
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      if (!this.track || !this.slides.length) return;

      this.stage = this.querySelector('[data-stage]');
      this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));
      this.thumbList = this.querySelector('[data-thumb-list]');
      this.prevButton = this.querySelector('[data-prev]');
      this.nextButton = this.querySelector('[data-next]');
      this.expandButton = this.querySelector('[data-expand]');
      this.countEl = this.querySelector('[data-count]');
      this.liveEl = this.querySelector('[id^="GalleryStatus"]');
      this.liveTemplate = this.dataset.liveTemplate || 'Image [index] of [total]';
      this.index = 0;
      this.target = null;
      this.frame = 0;
      this.targetTimer = 0;
      this.ready = true;
      this.abort = new AbortController();
      var signal = this.abort.signal;

      this.track.addEventListener('scroll', this.onScroll.bind(this), { passive: true, signal: signal });
      this.track.addEventListener('scrollend', this.onScrollEnd.bind(this), { signal: signal });

      this.thumbs.forEach(
        function (button, i) {
          button.addEventListener('click', this.goTo.bind(this, i, { play: true }), { signal: signal });
        }.bind(this)
      );
      if (this.prevButton) this.prevButton.addEventListener('click', this.step.bind(this, -1), { signal: signal });
      if (this.nextButton) this.nextButton.addEventListener('click', this.step.bind(this, 1), { signal: signal });
      if (this.stage) {
        this.stage.addEventListener('keydown', this.onKeydown.bind(this), { signal: signal });
        this.stage.addEventListener('click', this.onStageClick.bind(this), { signal: signal });
      }

      if (this.thumbList) {
        this.thumbList.addEventListener('scroll', this.updateThumbFade.bind(this), { passive: true, signal: signal });
      }
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.onResize.bind(this));
        this.resizeObserver.observe(this.track);
        if (this.thumbList) this.resizeObserver.observe(this.thumbList);
      }

      if (
        typeof subscribe === 'function' &&
        typeof PUB_SUB_EVENTS !== 'undefined' &&
        PUB_SUB_EVENTS &&
        PUB_SUB_EVENTS.variantChange
      ) {
        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this));
      }

      this.initLightbox(signal);
      this.setActive(0, false);
      this.updateThumbFade();
      this.setAttribute('data-ready', '');

      // neighbours of the current image are fetched early, but only once the page (LCP image first) has loaded
      this.warmed = false;
      var warm = function () {
        this.warmed = true;
        if (this.ready) this.loadNear(this.index);
      }.bind(this);
      if (document.readyState === 'complete') warm();
      else window.addEventListener('load', warm, { once: true, signal: signal });
    }

    destroy() {
      if (!this.ready) return;
      this.ready = false;
      this.abort.abort();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.unsubscribeVariant) this.unsubscribeVariant();
      cancelAnimationFrame(this.frame);
      clearTimeout(this.targetTimer);
      if (this.lbOpen) {
        if (typeof this.lightbox.close === 'function' && this.lightbox.open) this.lightbox.close();
        this.finishLightbox(false);
      }
      this.removeAttribute('data-ready');
    }

    /* ---------- state ---------- */

    setActive(i, announce) {
      var changed = i !== this.index;
      this.index = i;

      this.slides.forEach(function (slide, n) {
        var on = n === i;
        slide.toggleAttribute('data-active', on);
        slide.toggleAttribute('inert', !on);
      });
      this.thumbs.forEach(function (button, n) {
        if (n === i) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });

      var slide = this.slides[i];
      if (this.expandButton) this.expandButton.hidden = slide.dataset.mediaType !== 'image';
      if (this.countEl) this.countEl.textContent = i + 1 + ' / ' + this.slides.length;

      this.loadNear(i);
      this.keepThumbVisible(i);

      if (changed) {
        pauseMedia();
        if (announce && this.liveEl) {
          this.liveEl.textContent = this.liveTemplate
            .replace('[index]', String(i + 1))
            .replace('[total]', String(this.slides.length));
        }
        this.dispatchEvent(
          new CustomEvent('noma-gallery:change', { bubbles: true, detail: { index: i, mediaId: slide.dataset.mediaId } })
        );
      }
    }

    /* The first image is eager; the neighbours of the current one are fetched early so a click never shows a blank. */
    loadNear(i) {
      if (!this.warmed) return;
      [i - 1, i, i + 1].forEach(
        function (n) {
          var slide = this.slides[n];
          var img = slide && slide.querySelector('img[loading="lazy"]');
          if (img) img.loading = 'eager';
        }.bind(this)
      );
    }

    goTo(i, options) {
      options = options || {};
      i = clamp(i, 0, this.slides.length - 1);
      var left = this.slides[i].offsetLeft;
      var smooth = options.smooth !== false && !prefersReducedMotion();
      var distance = Math.abs(this.track.scrollLeft - left);

      // while a programmatic scroll runs, scroll events must not move the active state through the slides in between
      clearTimeout(this.targetTimer);
      this.target = distance > 1 ? i : null;
      if (this.target !== null) {
        this.targetTimer = setTimeout(
          function () {
            this.target = null;
          }.bind(this),
          900
        );
      }

      this.setActive(i, true);
      if (distance > 1) this.track.scrollTo({ left: left, behavior: smooth ? 'smooth' : 'auto' });
      if (options.play) this.playSlide(i);
    }

    step(direction) {
      var last = this.slides.length - 1;
      var next = this.index + direction;
      var wrapped = next < 0 || next > last;
      if (next < 0) next = last;
      if (next > last) next = 0;
      this.goTo(next, { smooth: !wrapped });
    }

    /* Dawn's media-gallery API: product-info (or another script) can ask for a media id "<section>-<media id>". */
    setActiveMedia(mediaId) {
      for (var n = 0; n < this.slides.length; n++) {
        if (this.slides[n].dataset.mediaId === String(mediaId)) {
          this.goTo(n, { smooth: false });
          return;
        }
      }
    }

    playSlide(i) {
      var deferred = this.slides[i].querySelector('.deferred-media');
      if (deferred && typeof deferred.loadContent === 'function') {
        pauseMedia();
        deferred.loadContent(false);
      }
    }

    /* ---------- events ---------- */

    onScroll() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(
        function () {
          this.frame = 0;
          this.syncFromScroll(false);
        }.bind(this)
      );
    }

    onScrollEnd() {
      this.target = null;
      this.syncFromScroll(true);
    }

    syncFromScroll(settled) {
      var width = this.track.clientWidth;
      if (!width) return;
      var position = Math.abs(this.track.scrollLeft);
      var i = clamp(Math.round(position / width), 0, this.slides.length - 1);
      if (this.target !== null) {
        if (!settled && Math.abs(position - this.slides[this.target].offsetLeft) > 2) return;
        this.target = null;
      }
      if (i !== this.index) this.setActive(i, true);
    }

    onResize() {
      if (!this.ready) return;
      // keep the current image in place when the breakpoint (and so the slide width) changes
      var left = this.slides[this.index].offsetLeft;
      if (this.target === null && Math.abs(this.track.scrollLeft - left) > 1) this.track.scrollTo({ left: left, behavior: 'auto' });
      this.updateThumbFade();
    }

    onKeydown(event) {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      if (event.target.closest && event.target.closest('video, iframe, model-viewer, input, textarea, select')) return;
      if (this.slides.length < 2) return;
      if (event.key === 'ArrowLeft') this.step(-1);
      else if (event.key === 'ArrowRight') this.step(1);
      else if (event.key === 'Home') this.goTo(0);
      else if (event.key === 'End') this.goTo(this.slides.length - 1);
      else return;
      event.preventDefault();
    }

    onStageClick(event) {
      if (!this.lightbox || event.target.closest('button, a')) return;
      var slide = event.target.closest('[data-slide]');
      if (slide && slide.dataset.mediaType === 'image') this.openLightbox(this.index, this.track);
    }

    onVariantChange(payload) {
      try {
        var data = payload && payload.data;
        if (!data || String(data.sectionId) !== String(this.dataset.section)) return;
        var media = data.variant && data.variant.featured_media;
        if (media && media.id) this.setActiveMedia(this.dataset.section + '-' + media.id);
      } catch (error) {
        /* a variant without featured media simply keeps the current image */
      }
    }

    /* ---------- thumbnails ---------- */

    keepThumbVisible(i) {
      var list = this.thumbList;
      var thumb = this.thumbs[i];
      if (!list || !thumb) return;
      var listRect = list.getBoundingClientRect();
      var thumbRect = thumb.getBoundingClientRect();
      var margin = 36; // clear of the soft edge (css fade) at both ends
      var top = 0;
      var left = 0;
      if (list.scrollHeight > list.clientHeight + 1) {
        if (thumbRect.top < listRect.top + margin) top = thumbRect.top - listRect.top - margin;
        else if (thumbRect.bottom > listRect.bottom - margin) top = thumbRect.bottom - listRect.bottom + margin;
      }
      if (list.scrollWidth > list.clientWidth + 1) {
        if (thumbRect.left < listRect.left + margin) left = thumbRect.left - listRect.left - margin;
        else if (thumbRect.right > listRect.right - margin) left = thumbRect.right - listRect.right + margin;
      }
      if (!top && !left) return;
      list.scrollBy({ top: top, left: left, behavior: this.ready && !prefersReducedMotion() ? 'smooth' : 'auto' });
    }

    /* data-fade-start / data-fade-end tell the css where more thumbnails are hidden (soft edge instead of a scrollbar) */
    updateThumbFade() {
      var list = this.thumbList;
      if (!list) return;
      var vertical = list.scrollHeight > list.clientHeight + 1;
      var position = vertical ? list.scrollTop : Math.abs(list.scrollLeft);
      var size = vertical ? list.clientHeight : list.clientWidth;
      var total = vertical ? list.scrollHeight : list.scrollWidth;
      list.toggleAttribute('data-fade-start', position > 2);
      list.toggleAttribute('data-fade-end', position + size < total - 2);
    }

    /* ---------- lightbox ---------- */

    initLightbox(signal) {
      this.lightbox = this.querySelector('[data-lightbox]');
      this.imageSlides = this.slides.filter(function (slide) {
        return slide.dataset.mediaType === 'image' && slide.dataset.full;
      });
      if (!this.lightbox || !this.imageSlides.length) {
        this.lightbox = null;
        if (this.expandButton) this.expandButton.hidden = true;
        this.expandButton = null;
        return;
      }

      var lb = this.lightbox;
      this.lbFigure = lb.querySelector('[data-lb-figure]');
      this.lbCount = lb.querySelector('[data-lb-count]');
      this.lbLive = lb.querySelector('[data-lb-live]');
      this.lbOpen = false;
      this.lbIndex = 0;
      this.lbToken = 0;

      if (this.expandButton) {
        this.expandButton.addEventListener(
          'click',
          function () {
            this.openLightbox(this.index, this.expandButton);
          }.bind(this),
          { signal: signal }
        );
      }

      var close = lb.querySelector('[data-lb-close]');
      var prev = lb.querySelector('[data-lb-prev]');
      var next = lb.querySelector('[data-lb-next]');
      if (close) close.addEventListener('click', this.closeLightbox.bind(this), { signal: signal });
      if (prev) prev.addEventListener('click', this.lightboxStep.bind(this, -1), { signal: signal });
      if (next) next.addEventListener('click', this.lightboxStep.bind(this, 1), { signal: signal });

      lb.addEventListener('keydown', this.onLightboxKeydown.bind(this), { signal: signal });
      lb.addEventListener(
        'cancel',
        function (event) {
          event.preventDefault();
          this.closeLightbox();
        }.bind(this),
        { signal: signal }
      );
      lb.addEventListener('close', this.finishLightbox.bind(this, true), { signal: signal });

      // click on the dark area (not on the image or a button) closes; swipe left / right = next / previous, down = close
      var start = null;
      var swiped = false;
      lb.addEventListener(
        'pointerdown',
        function (event) {
          if ((event.pointerType === 'mouse' && event.button !== 0) || event.target.closest('button')) return;
          start = { x: event.clientX, y: event.clientY };
          swiped = false;
        },
        { signal: signal }
      );
      lb.addEventListener(
        'pointerup',
        function (event) {
          if (!start) return;
          var dx = event.clientX - start.x;
          var dy = event.clientY - start.y;
          start = null;
          if (Math.abs(dx) > SWIPE_DISTANCE && Math.abs(dx) > Math.abs(dy) * 1.3) {
            swiped = true;
            this.lightboxStep(dx < 0 ? 1 : -1);
          } else if (dy > SWIPE_DISTANCE * 2 && dy > Math.abs(dx) * 1.3) {
            swiped = true;
            this.closeLightbox();
          }
        }.bind(this),
        { signal: signal }
      );
      lb.addEventListener(
        'pointercancel',
        function () {
          start = null;
        },
        { signal: signal }
      );
      lb.addEventListener(
        'click',
        function (event) {
          if (swiped) {
            swiped = false;
            return;
          }
          if (event.target.closest('button')) return;
          // the <img> box fills the stage (so a small photo is scaled up); only a click on the painted picture keeps it open
          var shown = event.target.closest('.noma-lightbox__img');
          if (shown) {
            var box = paintedRect(shown);
            if (event.clientX >= box.left && event.clientX <= box.right && event.clientY >= box.top && event.clientY <= box.bottom) return;
          }
          this.closeLightbox();
        }.bind(this),
        { signal: signal }
      );
    }

    openLightbox(slideIndex, returnTo) {
      if (!this.lightbox || this.lbOpen) return;
      var slide = this.slides[slideIndex];
      var n = this.imageSlides.indexOf(slide);
      if (n < 0) return;

      this.lbReturn = returnTo || document.activeElement;
      this.lbOpen = true;
      pauseMedia();
      if (typeof this.lightbox.showModal === 'function') {
        if (!this.lightbox.open) this.lightbox.showModal();
      } else {
        this.lightbox.setAttribute('open', '');
      }
      document.documentElement.classList.add('noma-lightbox-open');
      this.showLightboxImage(n, false);
      var close = this.lightbox.querySelector('[data-lb-close]');
      if (close) close.focus();
    }

    closeLightbox() {
      if (!this.lbOpen) return;
      if (typeof this.lightbox.close === 'function' && this.lightbox.open) this.lightbox.close();
      this.finishLightbox(true);
    }

    /* idempotent: runs from the close button, Escape, the dialog's own close event and disconnectedCallback */
    finishLightbox(restoreFocus) {
      if (!this.lbOpen) return;
      this.lbOpen = false;
      this.lbToken++;
      if (this.lightbox.hasAttribute('open') && typeof this.lightbox.close !== 'function') this.lightbox.removeAttribute('open');
      document.documentElement.classList.remove('noma-lightbox-open');
      var slide = this.imageSlides[this.lbIndex];
      var i = this.slides.indexOf(slide);
      if (restoreFocus === true && i > -1 && i !== this.index) this.goTo(i, { smooth: false });
      var target = this.lbReturn;
      this.lbReturn = null;
      if (restoreFocus === true && target && target.isConnected && typeof target.focus === 'function') {
        target.focus({ preventScroll: true });
      }
    }

    lightboxStep(direction) {
      this.showLightboxImage(this.lbIndex + direction, true);
    }

    showLightboxImage(n, announce) {
      var total = this.imageSlides.length;
      n = (n + total) % total;
      this.lbIndex = n;
      var slide = this.imageSlides[n];
      var thumbImg = slide.querySelector('img');
      var alt = thumbImg ? thumbImg.alt : '';
      var full = slide.dataset.full;
      var token = ++this.lbToken;

      this.lbCount.textContent = n + 1 + ' / ' + total;
      if (announce && this.lbLive) {
        this.lbLive.textContent = this.liveTemplate.replace('[index]', String(n + 1)).replace('[total]', String(total));
      }

      var img = this.lbFigure.querySelector('img');
      if (!img) {
        img = document.createElement('img');
        img.className = 'noma-lightbox__img';
        img.decoding = 'async';
        img.draggable = false;
        this.lbFigure.appendChild(img);
      }
      img.alt = alt;
      // show the already loaded main image at once, swap to the large file when it has arrived
      if (thumbImg && thumbImg.complete && thumbImg.currentSrc) img.src = thumbImg.currentSrc;
      var loader = new Image();
      var apply = function () {
        if (token === this.lbToken) img.src = full;
      }.bind(this);
      loader.onload = apply;
      loader.onerror = apply;
      loader.src = full;

      if (total > 1) {
        [n - 1, n + 1].forEach(
          function (m) {
            var neighbour = this.imageSlides[(m + total) % total];
            if (neighbour && neighbour.dataset.full) new Image().src = neighbour.dataset.full;
          }.bind(this)
        );
      }
    }

    onLightboxKeydown(event) {
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeLightbox();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.lightboxStep(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.lightboxStep(1);
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.showLightboxImage(0, true);
      } else if (event.key === 'End') {
        event.preventDefault();
        this.showLightboxImage(this.imageSlides.length - 1, true);
      } else if (event.key === 'Tab') {
        // focus trap (the modal dialog already makes the page inert, this also covers browsers without showModal)
        var focusable = Array.from(this.lightbox.querySelectorAll('button:not([disabled])'));
        if (!focusable.length) return;
        var first = focusable[0];
        var last = focusable[focusable.length - 1];
        if (event.shiftKey && (document.activeElement === first || !this.lightbox.contains(document.activeElement))) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }
  }

  customElements.define('noma-gallery', NomaGallery);
})();
